/**
 * smart-variant-picker.js
 *
 * Handles intelligent variant selection for products with:
 *   1. Merged product navigation (sibling products linked via metaobject)
 *   2. Combo variant splitting (e.g. "DALI w/ 2700K" → Dimming + Light Temperature)
 *   3. Standard native variants (passed through unchanged)
 *
 * Priority: custom.variant_config metafield overrides all auto-detection.
 *
 * ─────────────────────────────────────────────────────────────────
 * OVERRIDE METAFIELD: custom.variant_config (type: json)
 * ─────────────────────────────────────────────────────────────────
 * Set this metafield on a product to override auto-detection for any option.
 * Only options listed here are overridden — others are still auto-detected.
 *
 * Structure:
 * {
 *   "options": [
 *     {
 *       "shopify_option": "Dimming and Light Temperature",
 *       // The exact option name as it appears in Shopify (case-sensitive)
 *
 *       "mode": "combo",
 *       // "combo"  → split this option's values into sub-selectors
 *       // "native" → render as-is, no splitting (suppresses auto-detection)
 *
 *       "delimiter": " w/ ",
 *       // The string to split on. Required when mode is "combo".
 *       // Common values: " w/ ", " and ", " / "
 *
 *       "split_into": ["Dimming", "Light Temperature"]
 *       // Labels for each sub-selector after splitting.
 *       // Order matches the split result: left of delimiter → index 0, right → index 1.
 *     },
 *     {
 *       "shopify_option": "Cord Colour and Canopy",
 *       "mode": "combo",
 *       "delimiter": " and ",
 *       "split_into": ["Cord Colour", "Canopy Style"]
 *     },
 *     {
 *       "shopify_option": "Colour",
 *       "mode": "native"
 *       // Forces this option to render as plain buttons, no splitting attempted.
 *     }
 *   ]
 * }
 *
 * ─────────────────────────────────────────────────────────────────
 * AUTO-DETECTION RULES (when no override is set for an option)
 * ─────────────────────────────────────────────────────────────────
 * An option is treated as a combo if ANY of these are true:
 *   - Option name contains " and " (e.g. "Dimming and Light Temperature")
 *   - Option values consistently contain " w/ " (e.g. "DALI w/ 2700K")
 *   - Option values consistently contain " and " (when name doesn't — values
 *     like "Black Cord and Flat Canopy")
 *
 * "Consistently" means >50% of values contain the delimiter.
 *
 * ─────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  // ─── Constants (defaults — overridden by smart-variant-config.json) ──────────

  let DELIMITERS = [' w/ ', ' and ', ' | ', ' / '];
  let CONSISTENCY_THRESHOLD = 0.5;
  let LABEL_SPLIT_DELIMITER = ' and ';

  // Option names that trigger swatch/image rendering instead of text pills
  // Add to smart-variant-config.json swatch_option_names array to extend
  let SWATCH_OPTION_NAMES = ['colour', 'color', 'glass', 'backplate finish'];

  // When false (default): custom metafield → product image → native swatch
  // When true: custom metafield → native swatch → product image
  // Can be overridden per-product via custom.variant_config metafield
  let PREFER_NATIVE_SWATCHES = false;

  /**
   * Load smart-variant-config.json from theme assets and apply settings.
   * Called once on init. Falls back to defaults if file not found.
   * Edit assets/smart-variant-config.json to customise without touching JS.
   */
  function loadConfig() {
    const scriptEl = document.querySelector('script[src*="smart-variant-picker"]');
    const assetBase = scriptEl
      ? scriptEl.src.replace(/smart-variant-picker\.js[^/]*$/, '')
      : '/cdn/shop/t/1/assets/';
    return fetch(assetBase + 'smart-variant-config.json?v=' + Date.now())
      .then((r) => r.json())
      .then((config) => {
        if (Array.isArray(config.delimiters) && config.delimiters.length) {
          DELIMITERS = config.delimiters;
        }
        if (typeof config.consistency_threshold === 'number') {
          CONSISTENCY_THRESHOLD = config.consistency_threshold;
        }
        if (typeof config.label_split_delimiter === 'string') {
          LABEL_SPLIT_DELIMITER = config.label_split_delimiter;
        }
        if (Array.isArray(config.swatch_option_names) && config.swatch_option_names.length) {
          SWATCH_OPTION_NAMES = config.swatch_option_names.map((n) => n.toLowerCase());
        }
        if (typeof config.prefer_native_swatches === 'boolean') {
          PREFER_NATIVE_SWATCHES = config.prefer_native_swatches;
        }
        console.log('[SVP] Config loaded. Delimiters:', DELIMITERS);
      })
      .catch(() => {
        console.log('[SVP] No config file found, using defaults.');
      });
  }

  // ─── Utility ─────────────────────────────────────────────────────────────────

  /**
   * Given an array of values and a delimiter, returns true if enough values
   * contain that delimiter to be considered a combo option.
   */
  function isConsistentDelimiter(values, delimiter) {
    const count = values.filter((v) => v.includes(delimiter)).length;
    return count / values.length > CONSISTENCY_THRESHOLD;
  }

  /**
   * Detect the most likely delimiter for an array of option values.
   * Returns the delimiter string or null if no combo pattern detected.
   */
  function detectDelimiter(values) {
    for (const delim of DELIMITERS) {
      if (isConsistentDelimiter(values, delim)) return delim;
    }
    return null;
  }

  /**
   * Split a combo value string into its parts using a delimiter.
   * Returns an array of trimmed strings.
   */
  function splitValue(value, delimiter) {
    return value.split(delimiter).map((s) => s.trim());
  }

  /**
   * Generate auto-detected labels for sub-selectors when no override is given.
   * Tries to derive them from the option name (split on " and ") or falls back
   * to generic "Option 1", "Option 2" etc.
   */
  function autoLabels(optionName, partCount, detectedDelimiter) {
    // Try the detected delimiter first (e.g. " | " in option name)
    if (detectedDelimiter && optionName.includes(detectedDelimiter)) {
      const parts = optionName.split(detectedDelimiter).map((s) => s.trim());
      if (parts.length === partCount) return parts;
    }
    // Fall back to the configured label split delimiter (default: " and ")
    if (optionName.includes(LABEL_SPLIT_DELIMITER)) {
      const parts = optionName.split(LABEL_SPLIT_DELIMITER).map((s) => s.trim());
      if (parts.length === partCount) return parts;
    }
    // Try all known delimiters as a last resort
    for (const delim of DELIMITERS) {
      if (optionName.includes(delim)) {
        const parts = optionName.split(delim).map((s) => s.trim());
        if (parts.length === partCount) return parts;
      }
    }
    return Array.from({ length: partCount }, (_, i) => `Option ${i + 1}`);
  }

  // ─── Option Analysis ─────────────────────────────────────────────────────────

  /**
   * Analyse a single product option and return a descriptor object:
   * {
   *   shopifyOption: string,   // original Shopify option name
   *   mode: 'native' | 'combo',
   *   delimiter: string | null,
   *   labels: string[],        // sub-selector labels (length 1 if native)
   *   uniqueValues: string[],  // all unique values for this option
   *   splitMap: Map            // value → [part0, part1, ...] (empty if native)
   * }
   */
  function analyseOption(option, overrideConfig) {
    const values = option.values.map((v) => String(v));
    const unique = [...new Set(values)];

    // Check for override first
    const override = overrideConfig
      ? overrideConfig.find((o) => o.shopify_option === option.name)
      : null;

    if (override) {
      if (override.mode === 'native') {
        return {
          shopifyOption: option.name,
          mode: 'native',
          delimiter: null,
          labels: [option.name],
          uniqueValues: unique,
          splitMap: new Map(),
        };
      }
      if (override.mode === 'combo') {
        const delim = override.delimiter;
        const labels = override.split_into || autoLabels(option.name, 2);
        const splitMap = new Map(unique.map((v) => [v, splitValue(v, delim)]));
        return {
          shopifyOption: option.name,
          mode: 'combo',
          delimiter: delim,
          labels,
          uniqueValues: unique,
          splitMap,
        };
      }
    }

    // Auto-detection
    // A combo split requires BOTH:
    //   1. A delimiter detected consistently in the option values, AND
    //   2. Either the option name also contains a delimiter, or the values
    //      contain a non-name delimiter (e.g. " w/ ")
    //
    // This prevents option names that happen to contain " and " (e.g.
    // "Bottom Shade and Top Shade") from being split when their values
    // are plain strings with no delimiter (e.g. ["White", "Black"]).
    const nameHasAnd = option.name.includes(' and ');
    const detectedDelim = detectDelimiter(unique);

    // If the detected delimiter is " and ", require the option name to also
    // contain " and " before treating it as a combo -- " and " is too common
    // in plain finish/colour names (e.g. "Black and Chrome") to be trusted alone.
    const effectiveDelim = (detectedDelim === ' and ' && !nameHasAnd) ? null : detectedDelim;

    if (effectiveDelim) {
      // Values confirm a delimiter -- proceed with combo split
      const delim = effectiveDelim;
      // Verify the delimiter actually splits the values meaningfully
      const testSplit = splitValue(unique[0], delim);
      if (testSplit.length >= 2) {
        const splitMap = new Map(unique.map((v) => [v, splitValue(v, delim)]));
        const partCount = testSplit.length;
        const labels = autoLabels(option.name, partCount, delim);
        return {
          shopifyOption: option.name,
          mode: 'combo',
          delimiter: delim,
          labels,
          uniqueValues: unique,
          splitMap,
        };
      }
    } else if (nameHasAnd) {
      // Name suggests a combo (e.g. "Dimming and Light Temperature") but values
      // don't contain a detected delimiter -- only split if values contain " and "
      // themselves, confirming both name and values agree on the split.
      if (isConsistentDelimiter(unique, ' and ')) {
        const testSplit = splitValue(unique[0], ' and ');
        if (testSplit.length >= 2) {
          const splitMap = new Map(unique.map((v) => [v, splitValue(v, ' and ')]));
          const partCount = testSplit.length;
          const labels = autoLabels(option.name, partCount, ' and ');
          return {
            shopifyOption: option.name,
            mode: 'combo',
            delimiter: ' and ',
            labels,
            uniqueValues: unique,
            splitMap,
          };
        }
      }
    }

    // Native
    return {
      shopifyOption: option.name,
      mode: 'native',
      delimiter: null,
      labels: [option.name],
      uniqueValues: unique,
      splitMap: new Map(),
    };
  }

  // ─── Variant Resolution ───────────────────────────────────────────────────────

  /**
   * Given the current sub-selector selections (a map of shopifyOptionName →
   * reconstructed combo value, or native value), find the matching variant.
   *
   * selections: { [shopifyOptionName]: string }
   * variants: Shopify variant objects
   * optionDescriptors: analyseOption results
   */
  function findMatchingVariant(selections, variants, optionDescriptors) {
    return variants.find((variant) => {
      return optionDescriptors.every((desc, index) => {
        const optionKey = `option${index + 1}`;
        const variantValue = variant[optionKey];
        const selectedValue = selections[desc.shopifyOption];
        return variantValue === selectedValue;
      });
    });
  }

  /**
   * Given sub-selector state for a combo option, reconstruct the Shopify
   * variant value by finding which unique combo value matches all selected parts.
   *
   * subSelections: { [label]: selectedPart }  e.g. { "Dimming": "DALI", "Light Temperature": "2700K" }
   * desc: option descriptor
   */
  function reconstructComboValue(subSelections, desc) {
    for (const [comboValue, parts] of desc.splitMap.entries()) {
      const matches = desc.labels.every((label, i) => {
        return subSelections[label] === parts[i];
      });
      if (matches) return comboValue;
    }
    return null;
  }

  // ─── Smart Variant Picker Class ───────────────────────────────────────────────

  class SmartVariantPicker {
    constructor(containerEl) {
      this.container = containerEl;

      // Read product data from <script type="application/json"> tags
      // (avoids HTML attribute escaping issues with JSON in data attributes)
      const sectionId = containerEl.querySelector('.svp-mount').dataset.sectionId;

      const productDataEl = document.getElementById('svp-product-data-' + sectionId);
      const variantConfigEl = document.getElementById('svp-variant-config-' + sectionId);

      if (!productDataEl) {
        console.error('[SVP] Could not find product data script tag for section', sectionId);
        return;
      }

      this.productData = JSON.parse(productDataEl.textContent);
      const variantConfigData = variantConfigEl
        ? JSON.parse(variantConfigEl.textContent)
        : null;
      // Store options array separately for combo overrides
      this.overrideConfig = variantConfigData?.options || null;
      // Store full config for product-level flags like prefer_native_swatches
      this.productConfig = variantConfigData || {};

      this.variants = this.productData.variants;
      this.options = this.productData.options_with_values || this._buildOptionsWithValues();
      this.vendor = this.productData.vendor || null;
      // Strip merged variant suffix e.g. "Bola Lantern - Black" → "Bola Lantern"
      // so product-level swatch keys only need the base title.
      const rawTitle = this.productData.title || null;
      this.productTitle = rawTitle && rawTitle.includes(' - ')
        ? rawTitle.slice(0, rawTitle.lastIndexOf(' - ')).trim()
        : rawTitle;

      // Store instance reference so SwatchColourSystem can re-render once cache is warm
      this.container.__svpInstance = this;

      // Pre-fetch swatch colour database. Re-render once loaded in case the initial
      // render ran before the cache was populated (race condition on fast-loading products)
      SwatchColourSystem.getContent().then(() => this._render());

      // Analyse each Shopify option
      this.descriptors = this.options.map((opt) => analyseOption(opt, this.overrideConfig));

      // Current selections state
      // For native options: { shopifyOptionName: selectedValue }
      // For combo options: { shopifyOptionName: { label: selectedPart, ... } }
      this.nativeSelections = {};
      this.comboSelections = {};

      this._initSelections();
      this._restoreFromURL(); // override selections if ?variant= in URL
      this._render();
      this._bindEvents();

      // Shopify's Liquid loop caps at 100 variants regardless of product size.
      // Always fetch the full variant list via the product JSON endpoint to ensure
      // all variants are available for matching, then re-render with complete data.
      this._fetchAllVariants();
    }

    /**
     * Fallback: build options_with_values from variants if not provided directly.
     */
    _buildOptionsWithValues() {
      const optionNames = this.productData.options || [];
      return optionNames.map((name, index) => {
        const key = `option${index + 1}`;
        const values = [...new Set(this.variants.map((v) => v[key]).filter(Boolean))];
        return { name, values, selected_value: values[0] };
      });
    }

    /**
     * Initialise selections from the currently selected variant.
     */
    _initSelections() {
      const current = this.variants.find((v) => v.id === this.productData.selected_variant_id)
        || this.variants.find((v) => v.available)
        || this.variants[0];

      this.descriptors.forEach((desc, index) => {
        const key = `option${index + 1}`;
        const currentValue = current ? current[key] : desc.uniqueValues[0];

        if (desc.mode === 'native') {
          this.nativeSelections[desc.shopifyOption] = currentValue;
        } else {
          const parts = desc.splitMap.get(currentValue) || [];
          this.comboSelections[desc.shopifyOption] = {};
          desc.labels.forEach((label, i) => {
            this.comboSelections[desc.shopifyOption][label] = parts[i] || null;
          });
        }
      });
    }

    /**
     * Build the full set of unique sub-values for a given label within a combo,
     * filtered to only those available given current other selections.
     */
    _getAvailableSubValues(desc, targetLabel) {
      const targetLabelIndex = desc.labels.indexOf(targetLabel);
      const currentSubSelections = this.comboSelections[desc.shopifyOption] || {};
      const optionIndex = this.descriptors.indexOf(desc);
      const optionKey = `option${optionIndex + 1}`;

      // Find all variants that are available and consistent with current
      // sub-selections for OTHER labels in this combo (not the target label)
      const compatibleVariants = this.variants.filter((variant) => {
        if (!variant.available) return false;
        const comboValue = variant[optionKey];
        if (!comboValue) return false;
        const parts = desc.splitMap.get(comboValue) || [];
        if (!parts.length) return false;

        return desc.labels.every((label, i) => {
          if (label === targetLabel) return true; // skip target
          const currentSub = currentSubSelections[label];
          if (!currentSub) return true; // nothing selected for this label yet
          return parts[i] === currentSub;
        });
      });

      // Extract unique sub-values for the target label from compatible variants
      const subValues = [...new Set(
        compatibleVariants.map((variant) => {
          const comboValue = variant[optionKey];
          return (desc.splitMap.get(comboValue) || [])[targetLabelIndex];
        }).filter(Boolean)
      )];

      return subValues;
    }

    /**
     * Get all available variant option values for a native option.
     * A value is available if any variant with that value exists and is available.
     */
    _getAvailableNativeValues(desc, descriptorIndex) {
      const key = `option${descriptorIndex + 1}`;

      // Only check selections from options ABOVE this one in the hierarchy.
      // Downstream selections (lower index options) should not constrain upstream ones —
      // e.g. Colour availability should not be affected by a Downrod selection.
      const upstreamSelections = this.descriptors
        .map((d, i) => ({ d, i }))
        .filter(({ d, i }) => i < descriptorIndex && d.mode === 'native')
        .map(({ d, i }) => ({
          key: `option${i + 1}`,
          value: this.nativeSelections[d.shopifyOption],
        }))
        .filter(({ value }) => !!value);

      return desc.uniqueValues.filter((value) =>
        this.variants.some((v) => {
          if (v[key] !== value) return false;
          if (!v.available) return false;
          // Must match upstream selections only
          return upstreamSelections.every(({ key: upKey, value: upValue }) =>
            v[upKey] === upValue
          );
        })
      );
    }

    /**
     * Reconstruct the full { shopifyOptionName: comboValue } selections map,
     * merging in any overrides provided.
     */
    _buildFullSelections(overrides = {}) {
      const selections = {};
      this.descriptors.forEach((desc) => {
        if (overrides[desc.shopifyOption] !== undefined) {
          selections[desc.shopifyOption] = overrides[desc.shopifyOption];
          return;
        }
        if (desc.mode === 'native') {
          selections[desc.shopifyOption] = this.nativeSelections[desc.shopifyOption];
        } else {
          selections[desc.shopifyOption] = reconstructComboValue(
            this.comboSelections[desc.shopifyOption] || {},
            desc
          );
        }
      });
      return selections;
    }

    /**
     * Render all selectors into the container.
     */
    _render() {
      // Clear existing rendered selectors (not the hidden variant input)
      this.container.querySelectorAll('.svp-option-group').forEach((el) => el.remove());

      this.descriptors.forEach((desc, index) => {
        if (desc.mode === 'native') {
          this.container.appendChild(this._renderNativeOption(desc, index));
        } else {
          // Render one sub-selector per label
          desc.labels.forEach((label) => {
            this.container.appendChild(this._renderComboSubSelector(desc, label));
          });
        }
      });

      this._updateVariantInput();
      attachTooltips(this.container);
    }

    /**
     * Build a map of { colourValue: swatchData } for swatch rendering.
     *
     * Default priority (prefer_native_swatches: false):
     *   1. custom_swatch_image metafield
     *   2. Product-level swatch (swatch-colours.json products[productTitle])
     *   3. Brand-level swatch (swatch-colours.json brands[vendor])
     *   4. Global swatch (swatch-colours.json global)
     *   5. Variant featured_image (product photo)
     *   6. Native Shopify swatch image
     *   7. Native Shopify swatch hex colour
     *
     * When prefer_native_swatches: true:
     *   1. custom_swatch_image metafield
     *   2. Product-level swatch
     *   3. Brand-level swatch
     *   4. Global swatch
     *   5. Native Shopify swatch image
     *   6. Native Shopify swatch hex colour
     *   7. Variant featured_image (product photo)
     *
     * Returns { [value]: { type: 'image'|'color', src: string } }
     */
    _buildSwatchMap(optionIndex) {
      const key = `option${optionIndex + 1}`;
      const optionData = this.options[optionIndex];
      const nativeSwatches = optionData?.swatches || {};

      // Check product-level override first, then global config
      const preferNative = this.productConfig?.prefer_native_swatches !== undefined
        ? this.productConfig.prefer_native_swatches
        : PREFER_NATIVE_SWATCHES;

      const map = {};

      this.variants.forEach((v) => {
        const val = v[key];
        if (!val || map[val]) return;

        // Priority order: product → brand → metafield → global → featured image → native

        // 1. Product or brand level database match
        const productBrandSwatch = SwatchColourSystem.resolveProductBrand(val, this.vendor, this.productTitle);
        if (productBrandSwatch) {
          map[val] = productBrandSwatch;
          return;
        }

        // 2. custom_swatch_image variant metafield
        if (v.swatch_image) {
          map[val] = { type: 'image', src: v.swatch_image };
          return;
        }

        // 3. Global database match
        const globalSwatch = SwatchColourSystem.resolveGlobal(val);
        if (globalSwatch) {
          map[val] = globalSwatch;
          return;
        }

        // 4. Fallbacks dependent on preferNative setting
        if (preferNative) {
          if (nativeSwatches[val]?.image) {
            map[val] = { type: 'image', src: nativeSwatches[val].image };
          } else if (nativeSwatches[val]?.color) {
            map[val] = { type: 'color', src: nativeSwatches[val].color };
          } else if (v.featured_image?.src) {
            map[val] = { type: 'image', src: v.featured_image.src };
          }
        } else {
          if (v.featured_image?.src) {
            map[val] = { type: 'image', src: v.featured_image.src };
          } else if (nativeSwatches[val]?.image) {
            map[val] = { type: 'image', src: nativeSwatches[val].image };
          } else if (nativeSwatches[val]?.color) {
            map[val] = { type: 'color', src: nativeSwatches[val].color };
          }
        }
      });
      return map;
    }

    /**
     * Get the count of values hidden for a given option based on upstream selections.
     * Returns { hidden: number, parentName: string|null }
     */
    _getHiddenInfo(desc, descriptorIndex) {
      const allValues = desc.uniqueValues;

      // Get available values with current upstream selections
      const available = this._getAvailableNativeValues(desc, descriptorIndex);
      const hidden = allValues.length - available.length;

      if (hidden === 0) return { hidden: 0, parentName: null };

      // Find which upstream options are actually causing the constraint
      // by checking availability with each upstream option removed in turn
      const upstreamDescriptors = this.descriptors
        .map((d, i) => ({ d, i }))
        .filter(({ i }) => i < descriptorIndex && this.descriptors[i].mode === 'native');

      const culprits = upstreamDescriptors
        .filter(({ d, i }) => {
          // Check how many values are available WITHOUT this upstream selection
          const key = `option${descriptorIndex + 1}`;
          const otherUpstream = upstreamDescriptors
            .filter(({ i: oi }) => oi !== i)
            .map(({ d: od, i: oi }) => ({
              key: `option${oi + 1}`,
              value: this.nativeSelections[od.shopifyOption],
            }))
            .filter(({ value }) => !!value);

          const availableWithout = desc.uniqueValues.filter((value) =>
            this.variants.some((v) => {
              if (v[key] !== value) return false;
              if (!v.available) return false;
              return otherUpstream.every(({ key: uk, value: uv }) => v[uk] === uv);
            })
          );

          // If more values are available without this option's constraint, it's a culprit
          return availableWithout.length > available.length;
        })
        .map(({ d }) => d.shopifyOption);

      const parentName = culprits.length > 0
        ? culprits.join(' and ')
        : upstreamDescriptors.length > 0
          ? upstreamDescriptors[upstreamDescriptors.length - 1].d.shopifyOption
          : null;

      return { hidden, parentName };
    }

    /**
     * Render a native (non-combo) option as pill buttons.
     * Colour/Color options render as image swatches instead of text pills.
     */
    _renderNativeOption(desc, descriptorIndex) {
      const isSwatchOption = SWATCH_OPTION_NAMES.map(n => n.toLowerCase()).includes(desc.shopifyOption.toLowerCase());
      const swatchMap = isSwatchOption ? this._buildSwatchMap(descriptorIndex) : {};

      const wrapper = document.createElement('fieldset');
      wrapper.className = `js product-form__input svp-option-group${isSwatchOption ? ' product-form__input--swatch-colour' : ' product-form__input--pill'}`;
      wrapper.dataset.shopifyOption = desc.shopifyOption;
      wrapper.dataset.mode = 'native';

      const legend = document.createElement('legend');
      legend.className = 'form__label';
      legend.textContent = `${desc.shopifyOption}:`;
      if (isSwatchOption) {
        const selectedSpan = document.createElement('span');
        selectedSpan.className = 'svp-swatch-selected-value';
        selectedSpan.textContent = ` ${this.nativeSelections[desc.shopifyOption] || ''}`;
        legend.appendChild(selectedSpan);
      }
      wrapper.appendChild(legend);

      const group = document.createElement('div');
      group.className = 'product-form__input--radio-group';

      const availableValues = this._getAvailableNativeValues(desc, descriptorIndex);
      const selectedValue = this.nativeSelections[desc.shopifyOption];

      desc.uniqueValues.forEach((value) => {
        const isAvailable = availableValues.includes(value);
        const isSelected = value === selectedValue;
        const inputId = `svp-${desc.shopifyOption.replace(/\s+/g, '-')}-${value.replace(/\s+/g, '-')}`.toLowerCase();

        // Hide unavailable options unless:
        // - This is the first option (always show all — nothing upstream to constrain it)
        // - The value is currently selected (always keep visible so customer can change away)
        if (descriptorIndex > 0 && !isAvailable && !isSelected) return;

        const item = document.createElement('div');
        item.className = `product-form__input--pill metafield-options__item radio-option${isSwatchOption ? ' svp-swatch-item' : ''}`;
        // Only mark unavailable on non-first options — top tier always looks fully available
        if (!isAvailable && descriptorIndex > 0) item.classList.add('svp-unavailable');

        const input = document.createElement('input');
        input.type = 'radio';
        input.id = inputId;
        input.name = `svp-native-${desc.shopifyOption}`;
        input.value = value;
        input.checked = isSelected;
        input.className = 'radio__input svp-native-input';
        input.dataset.shopifyOption = desc.shopifyOption;

        const label = document.createElement('label');
        label.htmlFor = inputId;
        label.className = isSwatchOption ? 'radio__label variant-button variant-button--colour' : 'radio__label';
        if (isSwatchOption) label.dataset.swatchValue = value;

        const swatchData = swatchMap[value];
        if (isSwatchOption && swatchData?.type === 'image') {
          const img = document.createElement('img');
          img.className = 'variant-button__image';
          img.src = swatchData.src;
          img.alt = value;
          img.loading = 'lazy';
          img.width = 50;
          img.height = 50;
          img.style.cssText = 'width:100%;height:100%;object-fit:cover;object-position:center;display:block;';
          label.appendChild(img);
        } else if (isSwatchOption && swatchData?.type === 'color') {
          label.style.setProperty('--swatch-hex', swatchData.src);
          label.classList.add('variant-button--colour-hex');
        } else {
          const span = document.createElement('span');
          span.className = 'variant-button__text';
          span.textContent = value;
          label.appendChild(span);
        }

        item.appendChild(input);
        item.appendChild(label);
        group.appendChild(item);
      });

      // Show asterisk tooltip on legend if any values are hidden (not for first option)
      const { hidden, parentName } = this._getHiddenInfo(desc, descriptorIndex);
      const existingAsterisk = legend.querySelector('.svp-hidden-asterisk');
      if (existingAsterisk) existingAsterisk.remove();
      if (hidden > 0 && descriptorIndex > 0) {
        const asteriskWrapper = document.createElement('span');
        asteriskWrapper.className = 'svp-hidden-asterisk';

        const asterisk = document.createElement('button');
        asterisk.type = 'button';
        asterisk.className = 'svp-tooltip-trigger svp-asterisk-trigger';
        asterisk.setAttribute('aria-label', 'Some options hidden');
        asterisk.setAttribute('aria-expanded', 'false');
        asterisk.textContent = '!';

        const bubble = document.createElement('span');
        bubble.className = 'svp-tooltip-bubble';
        bubble.setAttribute('role', 'tooltip');
        bubble.textContent = parentName
          ? `${hidden} ${desc.shopifyOption} option${hidden > 1 ? 's' : ''} not available for selected ${parentName}. Change ${parentName} to see more options.`
          : `${hidden} option${hidden > 1 ? 's' : ''} not available for current selection.`;

        asteriskWrapper.appendChild(asterisk);
        asteriskWrapper.appendChild(bubble);

        // Reuse tooltip show/hide behaviour
        asteriskWrapper.addEventListener('mouseenter', () => {
          asterisk.setAttribute('aria-expanded', 'true');
          bubble.classList.add('svp-tooltip-bubble--visible');
        });
        asteriskWrapper.addEventListener('mouseleave', () => {
          asterisk.setAttribute('aria-expanded', 'false');
          bubble.classList.remove('svp-tooltip-bubble--visible');
        });
        asterisk.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = asterisk.getAttribute('aria-expanded') === 'true';
          document.querySelectorAll('.svp-tooltip-bubble--visible').forEach(b => b.classList.remove('svp-tooltip-bubble--visible'));
          if (!isOpen) {
            asterisk.setAttribute('aria-expanded', 'true');
            bubble.classList.add('svp-tooltip-bubble--visible');
          }
        });

        legend.appendChild(asteriskWrapper);
      }

      wrapper.appendChild(group);
      return wrapper;
    }

    /**
     * Render a single sub-selector for one label within a combo option.
     */
    _renderComboSubSelector(desc, label) {
      const labelIndex = desc.labels.indexOf(label);
      const wrapper = document.createElement('fieldset');
      wrapper.className = 'js product-form__input product-form__input--pill svp-option-group svp-combo-group';
      wrapper.dataset.shopifyOption = desc.shopifyOption;
      wrapper.dataset.comboLabel = label;
      wrapper.dataset.mode = 'combo';

      const legend = document.createElement('legend');
      legend.className = 'form__label';
      legend.textContent = `${label}:`;
      wrapper.appendChild(legend);

      const group = document.createElement('div');
      group.className = 'product-form__input--radio-group';

      const availableSubValues = this._getAvailableSubValues(desc, label);
      const currentSub = (this.comboSelections[desc.shopifyOption] || {})[label];

      // Check if this sub-selector label should render as swatches
      const isSwatchLabel = SWATCH_OPTION_NAMES.map(n => n.toLowerCase()).includes(label.toLowerCase());

      // Show selected value next to label for swatch sub-selectors
      if (isSwatchLabel && currentSub) {
        const selectedSpan = document.createElement('span');
        selectedSpan.className = 'svp-swatch-selected-value';
        selectedSpan.textContent = ` ${currentSub}`;
        legend.appendChild(selectedSpan);
      }

      // Collect all unique sub-values for this label (not just available ones)
      const allSubValues = [...new Set(
        desc.uniqueValues.map((v) => (desc.splitMap.get(v) || [])[labelIndex]).filter(Boolean)
      )];

      // Build swatch map for this label if needed
      // Find which option index this combo belongs to
      const descIndex = this.descriptors.indexOf(desc);
      const optionKey = `option${descIndex + 1}`;
      const swatchMap = isSwatchLabel ? (() => {
        const map = {};
        const optionData = this.options[descIndex];
        const nativeSwatches = optionData?.swatches || {};
        const preferNative = this.productConfig?.prefer_native_swatches !== undefined
          ? this.productConfig.prefer_native_swatches
          : PREFER_NATIVE_SWATCHES;

        this.variants.forEach((v) => {
          const comboVal = v[optionKey];
          if (!comboVal) return;
          const parts = desc.splitMap.get(comboVal) || [];
          const subVal = parts[labelIndex];
          if (!subVal || map[subVal]) return;

          // For combo sub-selectors, skip custom_swatch_image — it belongs to the
          // combined variant, not the split sub-value. Use database directly.
          // Priority: product → brand → global (no metafield interleaving for combos)
          const dbSwatch = SwatchColourSystem.resolve(subVal, this.vendor, this.productTitle);
          if (dbSwatch) {
            map[subVal] = dbSwatch;
          } else if (preferNative) {
            if (nativeSwatches[subVal]?.image) map[subVal] = { type: 'image', src: nativeSwatches[subVal].image };
            else if (nativeSwatches[subVal]?.color) map[subVal] = { type: 'color', src: nativeSwatches[subVal].color };
            else if (v.featured_image?.src) map[subVal] = { type: 'image', src: v.featured_image.src };
          } else {
            if (v.featured_image?.src) map[subVal] = { type: 'image', src: v.featured_image.src };
            else if (nativeSwatches[subVal]?.image) map[subVal] = { type: 'image', src: nativeSwatches[subVal].image };
            else if (nativeSwatches[subVal]?.color) map[subVal] = { type: 'color', src: nativeSwatches[subVal].color };
          }
        });
        return map;
      })() : {};

      allSubValues.forEach((subValue) => {
        const isAvailable = availableSubValues.includes(subValue);
        const isSelected = subValue === currentSub;
        const inputId = `svp-${desc.shopifyOption.replace(/\s+/g, '-')}-${label.replace(/\s+/g, '-')}-${subValue.replace(/\s+/g, '-')}`.toLowerCase();

        // Hide unavailable sub-values unless:
        // - This is the first label of the first combo option (top tier — always show all)
        // - The value is currently selected (always keep visible)
        const isFirstCombo = descIndex === 0 && labelIndex === 0;
        if (!isFirstCombo && !isAvailable && !isSelected) return;

        const item = document.createElement('div');
        item.className = `product-form__input--pill metafield-options__item radio-option${isSwatchLabel ? ' svp-swatch-item' : ''}`;
        // Only mark unavailable on non-top-tier labels
        if (!isAvailable && !isFirstCombo) item.classList.add('svp-unavailable');

        const input = document.createElement('input');
        input.type = 'radio';
        input.id = inputId;
        input.name = `svp-combo-${desc.shopifyOption}-${label}`;
        input.value = subValue;
        input.checked = isSelected;
        input.className = 'radio__input svp-combo-input';
        input.dataset.shopifyOption = desc.shopifyOption;
        input.dataset.comboLabel = label;

        const labelEl = document.createElement('label');
        labelEl.htmlFor = inputId;
        labelEl.className = isSwatchLabel ? 'radio__label variant-button variant-button--colour' : 'radio__label';
        if (isSwatchLabel) labelEl.dataset.swatchValue = subValue;

        const swatchData = swatchMap[subValue];
        if (isSwatchLabel && swatchData?.type === 'image') {
          const img = document.createElement('img');
          img.className = 'variant-button__image';
          img.src = swatchData.src;
          img.alt = subValue;
          img.loading = 'lazy';
          img.width = 50;
          img.height = 50;
          img.style.cssText = 'width:100%;height:100%;object-fit:cover;object-position:center;display:block;';
          labelEl.appendChild(img);
        } else if (isSwatchLabel && swatchData?.type === 'color') {
          labelEl.style.setProperty('--swatch-hex', swatchData.src);
          labelEl.classList.add('variant-button--colour-hex');
        } else {
          const span = document.createElement('span');
          span.className = 'variant-button__text';
          span.textContent = subValue;
          labelEl.appendChild(span);
        }

        item.appendChild(input);
        item.appendChild(labelEl);
        group.appendChild(item);
      });

      // Show ! indicator if any sub-values are hidden
      const allSubCount = allSubValues.length;
      const hiddenSubCount = allSubCount - availableSubValues.length;
      const existingSubAsterisk = legend.querySelector('.svp-hidden-asterisk');
      if (existingSubAsterisk) existingSubAsterisk.remove();

      const isFirstComboLabel = descIndex === 0 && labelIndex === 0;
      if (hiddenSubCount > 0 && !isFirstComboLabel) {
        // Find other labels in this combo that are constraining this one
        const otherLabels = desc.labels.filter((l) => l !== label);
        const selectedOthers = otherLabels
          .filter((l) => (this.comboSelections[desc.shopifyOption] || {})[l])
          .join(' and ');

        const asteriskWrapper = document.createElement('span');
        asteriskWrapper.className = 'svp-hidden-asterisk';

        const asterisk = document.createElement('button');
        asterisk.type = 'button';
        asterisk.className = 'svp-tooltip-trigger svp-asterisk-trigger';
        asterisk.setAttribute('aria-label', 'Some options hidden');
        asterisk.setAttribute('aria-expanded', 'false');
        asterisk.textContent = '!';

        const bubble = document.createElement('span');
        bubble.className = 'svp-tooltip-bubble';
        bubble.setAttribute('role', 'tooltip');
        bubble.textContent = selectedOthers
          ? `${hiddenSubCount} ${label} option${hiddenSubCount > 1 ? 's' : ''} not available for selected ${selectedOthers}. Change ${selectedOthers} to see more options.`
          : `${hiddenSubCount} ${label} option${hiddenSubCount > 1 ? 's' : ''} not available for current selection.`;

        asteriskWrapper.appendChild(asterisk);
        asteriskWrapper.appendChild(bubble);

        asteriskWrapper.addEventListener('mouseenter', () => {
          asterisk.setAttribute('aria-expanded', 'true');
          bubble.classList.add('svp-tooltip-bubble--visible');
        });
        asteriskWrapper.addEventListener('mouseleave', () => {
          asterisk.setAttribute('aria-expanded', 'false');
          bubble.classList.remove('svp-tooltip-bubble--visible');
        });
        asterisk.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = asterisk.getAttribute('aria-expanded') === 'true';
          document.querySelectorAll('.svp-tooltip-bubble--visible').forEach(b => b.classList.remove('svp-tooltip-bubble--visible'));
          if (!isOpen) {
            asterisk.setAttribute('aria-expanded', 'true');
            bubble.classList.add('svp-tooltip-bubble--visible');
          }
        });

        legend.appendChild(asteriskWrapper);
      }

      wrapper.appendChild(group);
      return wrapper;
    }

    /**
     * Update the hidden variant ID input and fire the variant:change event
     * that the rest of the theme (buy buttons, SKU display, etc.) listens to.
     */
    _updateVariantInput() {
      const selections = this._buildFullSelections();
      const matched = findMatchingVariant(selections, this.variants, this.descriptors);

      const variantInput = this.container.closest('form')
        ? this.container.closest('form').querySelector('input[name="id"]')
        : document.querySelector('form[action*="/cart/add"] input[name="id"]');

      const selectedVariantEl = this.container.querySelector('[data-selected-variant]');

      if (matched) {
        if (variantInput) variantInput.value = matched.id;
        if (selectedVariantEl) selectedVariantEl.textContent = JSON.stringify(matched);
        this._syncURL(matched.id);
        console.log('[SVP] Matched variant:', matched.id, matched.sku);

        // Fire the same event the native picker fires so buy-buttons, SKU etc. update
        const event = new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: matched },
        });
        this.container.dispatchEvent(event);

        // Publish to Dawn's pub/sub system so media gallery, price etc. update
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.container.dataset.section,
              variant: matched,
              html: null,
            },
          });
        }

        // Update media gallery to show the variant's featured image
        this._updateMediaGallery(matched);

        this._updateSKUDisplay(matched);
        this._updatePriceDisplay(matched);
        this._updateAvailabilityState(matched);
      } else {
        // No variant found — all selections may not be complete yet
        this._updateSKUDisplay(null);
      }
    }

    /**
     * Update the media gallery to match the selected variant.
     *
     * Replicates Dawn's native updateMedia() approach:
     * 1. Fetches fresh section HTML with the selected variant
     * 2. Diffs the media gallery DOM (add/remove/reorder items)
     * 3. Calls setActiveMedia() to activate the variant's featured image
     *
     * This is the only reliable way to handle hide_variants and image switching
     * because the server controls which media items are shown per variant.
     */
    _updateMediaGallery(variant) {
      if (!variant) return;

      const productInfoEl = document.querySelector('product-info[data-section]');
      if (!productInfoEl) return;

      const sectionId = productInfoEl.dataset.section;
      const productUrl = productInfoEl.dataset.url;
      if (!productUrl || !sectionId) return;

      const requestUrl = `${productUrl}?variant=${variant.id}&section_id=${sectionId}`;

      fetch(requestUrl)
        .then((r) => r.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          const mediaGallerySource = productInfoEl.querySelector('media-gallery ul');
          const mediaGalleryDestination = html.querySelector('media-gallery ul');

          if (!mediaGallerySource || !mediaGalleryDestination) return;

          // Helper to get current source state
          const refreshSourceData = () => {
            const items = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
            const set = new Set(items.map((i) => i.dataset.mediaId));
            const map = new Map(items.map((i, idx) => [i.dataset.mediaId, { item: i, index: idx }]));
            return [items, set, map];
          };

          let [sourceItems, sourceSet, sourceMap] = refreshSourceData();
          const destItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
          const destSet = new Set(destItems.map((i) => i.dataset.mediaId));
          let shouldRefresh = false;

          // Add items from new data not in DOM
          for (let i = destItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(destItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(destItems[i]);
              shouldRefresh = true;
            }
          }

          // Remove items from DOM not in new data
          for (let i = 0; i < sourceItems.length; i++) {
            if (!destSet.has(sourceItems[i].dataset.mediaId)) {
              sourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [sourceItems, sourceSet, sourceMap] = refreshSourceData();

          // Reorder to match new data
          destItems.forEach((destItem, destIndex) => {
            const sourceData = sourceMap.get(destItem.dataset.mediaId);
            if (sourceData && sourceData.index !== destIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destIndex + 1})`)
              );
              [sourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });

          // Activate the variant's featured media
          if (variant.featured_media?.id) {
            const gallery = productInfoEl.querySelector('media-gallery');
            gallery?.setActiveMedia?.(`${sectionId}-${variant.featured_media.id}`, false);
          }
        })
        .catch((err) => {
          console.warn('[SVP] Media gallery update failed:', err);
        });
    }

    _updateSKUDisplay(variant) {
      const skuEl = document.getElementById(`Sku-${this.container.dataset.section}`);
      if (!skuEl) return;
      if (variant && variant.sku) {
        skuEl.textContent = variant.sku;
        skuEl.classList.remove('visibility-hidden');
      } else {
        skuEl.classList.add('visibility-hidden');
      }
    }

    _updatePriceDisplay(variant) {
      const priceEl = document.getElementById(`svp-variant-price-${this.container.dataset.section}`);
      if (!priceEl) return;
      if (variant && variant.price) {
        const formatted = (variant.price / 100).toLocaleString('en-AU', {
          style: 'currency',
          currency: 'AUD',
        });
        priceEl.textContent = formatted + ' AUD';
        priceEl.classList.remove('visibility-hidden');
      } else {
        priceEl.classList.add('visibility-hidden');
      }
    }

    _updateAvailabilityState(variant) {
      const submitBtn = document.querySelector('form[action*="/cart/add"] button[type="submit"]');
      if (!submitBtn) return;
      if (!variant || !variant.available) {
        submitBtn.disabled = true;
      } else {
        submitBtn.disabled = false;
      }
    }

    /**
     * Update the URL with the current variant ID as a ?variant= parameter,
     * matching Shopify's native behaviour.
     */
    _syncURL(variantId) {
      if (!variantId) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({ variantId }, '', url.toString());
    }

    /**
     * On init, check if a ?variant= param exists in the URL and restore
     * selections to match that variant.
     */
    _restoreFromURL() {
      const params = new URLSearchParams(window.location.search);
      const variantId = parseInt(params.get('variant'));
      if (!variantId) return false;

      const variant = this.variants.find((v) => v.id === variantId);
      if (!variant) return false;

      this.descriptors.forEach((desc, index) => {
        const key = 'option' + (index + 1);
        const value = variant[key];
        if (!value) return;

        if (desc.mode === 'native') {
          this.nativeSelections[desc.shopifyOption] = value;
        } else {
          const parts = desc.splitMap.get(value) || [];
          this.comboSelections[desc.shopifyOption] = {};
          desc.labels.forEach((label, i) => {
            this.comboSelections[desc.shopifyOption][label] = parts[i] || null;
          });
        }
      });

      return true;
    }

    /**
     * Bind change events on all sub-selectors.
     */
    _bindEvents() {
      this.container.addEventListener('change', (e) => {
        const input = e.target;

        if (input.classList.contains('svp-native-input')) {
          const optionName = input.dataset.shopifyOption;
          this.nativeSelections[optionName] = input.value;
          // Update selected value label for swatch options immediately (before rerender)
          const selectedSpan = this.container.querySelector(
            `[data-shopify-option="${optionName}"] .svp-swatch-selected-value`
          );
          if (selectedSpan) selectedSpan.textContent = ` ${input.value}`;
          this._rerender();
        }

        if (input.classList.contains('svp-combo-input')) {
          const optionName = input.dataset.shopifyOption;
          const label = input.dataset.comboLabel;
          if (!this.comboSelections[optionName]) this.comboSelections[optionName] = {};
          this.comboSelections[optionName][label] = input.value;
          this._rerender();
        }
      });
    }

    /**
     * Fetch all variants via /products/handle.js when Liquid's 100-variant
     * cap means we didn't get the full list on page load.
     * Re-renders once the full variant data is available.
     */
    _fetchAllVariants() {
      const productInfoEl = document.querySelector('product-info[data-url]');
      const productPath = productInfoEl
        ? productInfoEl.dataset.url.split('?')[0]
        : `/products/${this.productData.handle}`;
      const baseUrl = `${productPath}.js`;

      // Build a lookup of swatch_image values from Liquid variants
      // (metafields aren't available via storefront API so we preserve them)
      const swatchImageMap = {};
      this.variants.forEach((v) => {
        if (v.swatch_image) swatchImageMap[v.id] = v.swatch_image;
      });

      const mapVariant = (v) => ({
        id: v.id,
        available: v.available,
        sku: v.sku,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        price: v.price,
        compare_at_price: v.compare_at_price,
        featured_image: v.featured_image || null,
        featured_media: v.featured_media || null,
        // Preserve swatch_image from Liquid — not available via storefront API
        swatch_image: swatchImageMap[v.id] || null,
      });

      const fetchPage = (page, accumulated) => {
        const url = `${baseUrl}?page=${page}`;
        return fetch(url)
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.json();
          })
          .then((data) => {
            const variants = data.variants || [];
            if (!variants.length) return accumulated;
            const all = accumulated.concat(variants.map(mapVariant));
            if (variants.length >= 30) return fetchPage(page + 1, all);
            return all;
          });
      };

      console.log('[SVP] Fetching all variants from:', baseUrl);
      fetchPage(1, [])
        .then((allVariants) => {
          if (!allVariants.length) return;
          this.variants = allVariants;
          console.log(`[SVP] Fetched ${this.variants.length} total variants`);
          // Wait for swatch cache before rendering to avoid race condition
          // where fast-loading products render before swatch-colours.json is ready
          SwatchColourSystem.getContent().then(() => this._render());
        })
        .catch((err) => {
          console.warn('[SVP] Failed to fetch full variant list:', err);
        });
    }

    /**
     * Re-render after a selection change — preserves current selections
     * and updates availability of other options.
     */
    _rerender() {
      const scrollY = window.scrollY;
      this._render();
      window.scrollTo(0, scrollY);
    }
  }

  // ─── Merged Product Navigation ────────────────────────────────────────────────

  class MergedProductSelector {
    constructor(containerEl) {
      this.container = containerEl;
      this._bindEvents();
    }

    _bindEvents() {
      this.container.querySelectorAll('.svp-merged-link').forEach((link) => {
        link.addEventListener('click', (e) => {
          const hiddenInput = this.container.querySelector('.svp-merged-hidden');
          if (hiddenInput) {
            hiddenInput.value = link.dataset.optionValue;
          }
        });
      });
    }
  }

  // ─── Swatch Colour System ────────────────────────────────────────────────────

  /**
   * Loads swatch-colours.json and resolves swatch data per colour name, vendor,
   * and product title.
   *
   * Lookup chain per colour value:
   *   1. products[productTitle][colourName].image  — product-specific image
   *   2. products[productTitle][colourName].hex    — product-specific hex
   *   3. brands[vendor][colourName].image          — brand-specific image
   *   4. brands[vendor][colourName].hex            — brand-specific hex
   *   5. global[colourName].image                  — global image
   *   6. global[colourName].hex                    — global hex
   *   7. null (fall through to variant image / native swatch)
   *
   * Expected swatch-colours.json structure:
   * {
   *   "global": { "Black": { "image": "filename.png" } },
   *   "brands": { "RBW": { "Matte Black": { "image": "https://..." } } },
   *   "products": { "Ample": { "Frosted": { "image": "https://..." } } }
   * }
   *
   * Image values can be full URLs (http/https) or asset-relative filenames.
   * To add/edit swatches: edit assets/swatch-colours.json using the swatch manager tool.
   */
  const SwatchColourSystem = (function () {
    let _cache = null;
    let _fetchPromise = null;
    let _assetBase = null;

    function getAssetBase() {
      if (_assetBase) return _assetBase;
      const scriptEl = document.querySelector('script[src*="smart-variant-picker"]');
      _assetBase = scriptEl
        ? scriptEl.src.replace(/smart-variant-picker\.js[^/]*$/, '')
        : '/cdn/shop/t/1/assets/';
      return _assetBase;
    }

    function getContent() {
      if (_cache) return Promise.resolve(_cache);
      if (_fetchPromise) return _fetchPromise;

      _fetchPromise = fetch(getAssetBase() + 'swatch-colours.json?v=' + Date.now())
        .then((r) => r.json())
        .then((data) => { _cache = data; return data; })
        .catch(() => { _cache = { global: {}, brands: {}, products: {} }; return _cache; });

      return _fetchPromise;
    }

    /**
     * Resolve swatch entry for a colour name, vendor, and product title.
     * Returns { type: 'image'|'color', src: string } or null.
     *
     * @param {string} colourName   - The variant option value (e.g. "Frosted")
     * @param {string|null} vendor  - Product vendor (e.g. "RBW")
     * @param {string|null} productTitle - Product title (e.g. "Ample")
     */
    /**
     * Shared lookup helpers used by both resolve functions.
     */
    function _buildLookupHelpers() {
      const products = _cache.products || {};
      const brands = _cache.brands || {};
      const global = _cache.global || {};
      const base = getAssetBase();
      const toSrc = (img) =>
        (img.startsWith('http') || img.startsWith('//')) ? img : base + img;
      const norm = (s) => s ? s.replace(/ /g, ' ').trim() : s;
      return { products, brands, global, toSrc, norm };
    }

    /**
     * Resolve product and brand level only (steps 1-4).
     * Used in _buildSwatchMap before checking the variant metafield,
     * so the database takes priority over custom_swatch_image for product+brand.
     */
    function resolveProductBrand(colourName, vendor, productTitle) {
      if (!_cache) return null;
      const { products, brands, toSrc, norm } = _buildLookupHelpers();
      const normTitle = norm(productTitle);

      const productEntry = (normTitle && products[normTitle]?.[colourName])
        || (normTitle && vendor && products[vendor]?.[normTitle]?.[colourName])
        || null;

      // 1. Product-specific image
      if (productEntry?.image) return { type: 'image', src: toSrc(productEntry.image) };
      // 2. Product-specific hex
      if (productEntry?.hex) return { type: 'color', src: productEntry.hex };
      // 3. Brand image
      if (vendor && brands[vendor]?.[colourName]?.image) {
        return { type: 'image', src: toSrc(brands[vendor][colourName].image) };
      }
      // 4. Brand hex
      if (vendor && brands[vendor]?.[colourName]?.hex) {
        return { type: 'color', src: brands[vendor][colourName].hex };
      }
      return null;
    }

    /**
     * Resolve global level only (steps 5-6).
     * Used in _buildSwatchMap after checking variant metafield.
     */
    function resolveGlobal(colourName) {
      if (!_cache) return null;
      const { global, toSrc } = _buildLookupHelpers();
      // 5. Global image
      if (global[colourName]?.image) return { type: 'image', src: toSrc(global[colourName].image) };
      // 6. Global hex
      if (global[colourName]?.hex) return { type: 'color', src: global[colourName].hex };
      return null;
    }

    /**
     * Full resolve chain: product → brand → global.
     * Used by enhanceMergedSwatches and any other caller that doesn't
     * need to interleave the metafield check.
     *
     * Priority: product → brand → global
     */
    function resolve(colourName, vendor, productTitle) {
      if (!_cache) return null;
      return resolveProductBrand(colourName, vendor, productTitle)
        || resolveGlobal(colourName);
    }

    return { getContent, resolve, resolveProductBrand, resolveGlobal };
  })();

  // ─── Tooltip System ─────────────────────────────────────────────────────────

  /**
   * Loads tooltip-content.json and resolves tooltip text per label.
   *
   * Lookup chain:
   *   1. products[productId][label]    — product ID override
   *   2. products[productHandle][label] — product handle override
   *   3. global[label]                 — global default
   *   4. null                          — no tooltip
   */
  const TooltipSystem = (function () {
    let _cache = null;
    let _fetchPromise = null;

    function getContent() {
      if (_cache) return Promise.resolve(_cache);
      if (_fetchPromise) return _fetchPromise;

      const scriptEl = document.querySelector('script[src*="smart-variant-picker"]');
      const assetBase = scriptEl
        ? scriptEl.src.replace(/smart-variant-picker\.js[^/]*$/, '')
        : '/cdn/shop/t/1/assets/';
      console.log('[SVP] Fetching tooltips from:', assetBase + 'tooltip-content.json');
      _fetchPromise = fetch(assetBase + 'tooltip-content.json?v=' + Date.now())
        .then((r) => r.json())
        .then((data) => {
          console.log('[SVP] Tooltip data loaded:', data);
          _cache = data; return data;
        })
        .catch((err) => {
          console.warn('[SVP] Tooltip fetch failed:', err);
          _cache = { global: {}, products: {} }; return _cache;
        });

      return _fetchPromise;
    }

    function resolveText(label, data, productId, productHandle) {
      const products = data.products || {};
      const global = data.global || {};

      if (productId && !productId.startsWith('_') && products[productId]) {
        const t = products[productId][label];
        if (t) return t;
      }
      if (productHandle && !productHandle.startsWith('_') && products[productHandle]) {
        const t = products[productHandle][label];
        if (t) return t;
      }
      return global[label] || null;
    }

    function show(trigger, bubble) {
      trigger.setAttribute('aria-expanded', 'true');
      bubble.classList.add('svp-tooltip-bubble--visible');
      requestAnimationFrame(() => {
        const rect = bubble.getBoundingClientRect();
        if (rect.top < 8) {
          bubble.classList.add('svp-tooltip-bubble--below');
        } else {
          bubble.classList.remove('svp-tooltip-bubble--below');
        }
      });
    }

    function hide(trigger, bubble) {
      trigger.setAttribute('aria-expanded', 'false');
      bubble.classList.remove('svp-tooltip-bubble--visible');
    }

    function closeAll() {
      document.querySelectorAll('.svp-tooltip-bubble--visible').forEach((b) => {
        b.classList.remove('svp-tooltip-bubble--visible');
      });
      document.querySelectorAll('.svp-tooltip-trigger[aria-expanded="true"]').forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
      });
    }

    document.addEventListener('click', closeAll);

    function createTrigger(label, data, productId, productHandle) {
      const text = resolveText(label, data, productId, productHandle);
      if (!text) return null;

      const wrapper = document.createElement('span');
      wrapper.className = 'svp-tooltip-wrapper';

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'svp-tooltip-trigger';
      trigger.setAttribute('aria-label', 'More information about ' + label);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.textContent = '?';

      const bubble = document.createElement('span');
      bubble.className = 'svp-tooltip-bubble';
      bubble.setAttribute('role', 'tooltip');
      bubble.textContent = text;

      wrapper.appendChild(trigger);
      wrapper.appendChild(bubble);

      wrapper.addEventListener('mouseenter', () => show(trigger, bubble));
      wrapper.addEventListener('mouseleave', () => hide(trigger, bubble));

      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        closeAll();
        if (!isOpen) show(trigger, bubble);
      });

      return wrapper;
    }

    return { getContent, createTrigger };
  })();

  /**
   * Attach tooltip triggers to all legend elements in the smart picker container.
   */
  function attachTooltips(containerEl) {
    const productInfoEl = document.querySelector('product-info[data-product-id]');
    const productId = productInfoEl ? productInfoEl.dataset.productId : null;
    const productUrl = productInfoEl ? (productInfoEl.dataset.url || '') : '';
    const productHandle = productUrl.replace(/^\/products\//, '').split('?')[0] || null;

    const getLegendLabel = (legend) => Array.from(legend.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent)
      .join('')
      .trim()
      .replace(/:$/, '')
      .trim();

    TooltipSystem.getContent().then((data) => {
      containerEl.querySelectorAll('.svp-option-group:not(.svp-prerender) legend').forEach((legend) => {
        if (legend.querySelector('.svp-tooltip-wrapper:not(.svp-hidden-asterisk)')) return;
        const labelText = getLegendLabel(legend);
        const trigger = TooltipSystem.createTrigger(labelText, data, productId, productHandle);
        if (trigger) legend.appendChild(trigger);
      });
    });
  }

  function attachMergedSelectorTooltips() {
    const productInfoEl = document.querySelector('product-info[data-product-id]');
    const productId = productInfoEl ? productInfoEl.dataset.productId : null;
    const productUrl = productInfoEl ? (productInfoEl.dataset.url || '') : '';
    const productHandle = productUrl.replace(/^\/products\//, '').split('?')[0] || null;

    const getLegendLabel = (legend) => Array.from(legend.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent)
      .join('')
      .trim()
      .replace(/:$/, '')
      .trim();

    TooltipSystem.getContent().then((data) => {
      document.querySelectorAll('.smart-merged-selector legend').forEach((legend) => {
        if (legend.querySelector('.svp-tooltip-wrapper:not(.svp-hidden-asterisk)')) return;
        const labelText = getLegendLabel(legend);
        const trigger = TooltipSystem.createTrigger(labelText, data, productId, productHandle);
        if (trigger) legend.appendChild(trigger);
      });
    });
  }

  /**
   * Swap merged product selector swatch image srcs with resolved URLs from
   * SwatchColourSystem, where a match exists. Falls back to whatever Liquid
   * already rendered (sibling.images[2] or custom_swatch_image metafield).
   *
   * Colour name: the alt text on the existing img tag (set by Liquid to sibling_label)
   * Product title: base title with merged variant suffix stripped (e.g. "Dimple™ Dash")
   */
  function enhanceMergedSwatches() {
    const productDataEl = document.querySelector('script[id^="svp-product-data"]');
    if (!productDataEl) return;
    const productData = JSON.parse(productDataEl.textContent);
    const vendor = productData.vendor || null;
    const rawTitle = productData.title || null;

    // Strip merged variant suffix e.g. "Dimple™ Dash - Smoked Glass" → "Dimple™ Dash"
    const productTitle = rawTitle && rawTitle.includes(' - ')
      ? rawTitle.slice(0, rawTitle.lastIndexOf(' - ')).trim()
      : rawTitle;

    SwatchColourSystem.getContent().then(() => {
      document.querySelectorAll('.smart-merged-selector .svp-merged-link img').forEach((img) => {
        const colourName = img.alt;
        if (!colourName) return;
        const swatchData = SwatchColourSystem.resolve(colourName, vendor, productTitle);
        if (swatchData?.type === 'image') {
          img.src = swatchData.src;
          img.style.objectFit = 'cover';
          img.style.objectPosition = 'center';
          img.style.borderRadius = '50%';
          img.style.display = 'block';
        }
      });
    });
  }

  function init() {
    loadConfig().then(() => {
      document.querySelectorAll('.smart-variant-picker').forEach((el) => {
        new SmartVariantPicker(el);
      });

      document.querySelectorAll('.smart-merged-selector').forEach((el) => {
        new MergedProductSelector(el);
      });

      attachMergedSelectorTooltips();
      enhanceMergedSwatches();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
